import { clamp } from 'rise-ui/utils/number.js';
import { softSet, softSetProperties } from 'rise-ui/utils/object.js';

export class PixelIndexCursor {
  position = null;
  radius = 1;
  isActive = false;

  constructor(pixelIndex) {
    this.pixelIndex = pixelIndex;
  }

  _isActive = false;
  _count = 0;
  _size = 0;
  _width = 0;
  _domainLeft;
  _domainRight;
  _windowLeft = 0;
  _windowRight = 1;
  _left;
  _right;
  _indexes;
  _position = null;

  setPosition(position = null) {
    this.position = position;
    return this;
  }

  setRadius(radius) {
    this.radius = Math.max(1, radius);
    return this;
  }

  activate() {
    this.isActive = true;
    return this;
  }

  deactivate() {
    this.isActive = false;
    return this;
  }

  updateCursor() {
    const { isActive, pixelIndex: pi, position } = this;

    const hasIsActiveChange = softSet(this, '_isActive', isActive);
    let hasChange = hasIsActiveChange;

    if (!isActive) {
      return hasChange;
    }

    const { count, domain, size, width, window } = pi;

    hasChange = softSetProperties(this, {
      _count:       count,
      _domainLeft:  domain[0],
      _domainRight: domain[1],
      _size:        size,
      _width:       width,
      _windowLeft:  window[0],
      _windowRight: window[1],
      _position:    position,
    });

    if (this._indexes && hasChange) {
      this._indexes = null;
    }

    return hasChange;
  }

  get _closestIndexes() {
    let { _indexes: indexes, position } = this;
    if (indexes) {
      return indexes;
    }

    if (position === null) {
      return this._indexes = [ [], null ];
    }

    let { pixelIndex: pi, radius } = this;
    const { width } = pi;

    const r = Math.ceil(Math.max(1, radius));
    const p = clamp(0, Math.floor(position), width - 1);
    const min = Math.max(0, p - r);
    const max = Math.min(p + r, width - 1);

    // Look for data to left...
    let left = min;
    let leftIndex = [];
    for (let i = p; i >= min; --i) {
      const index = pi.getByPixel(i);
      if (index.length) {
        left = i;
        leftIndex = index;
        break;
      }
    }
    // Look for data to right...
    let right = max;
    let rightIndex = [];
    for (let i = p + 1; i <= max; ++i) {
      const index = pi.getByPixel(i);
      if (index.length) {
        right = i;
        rightIndex = index;
        break;
      }
    }

    // Set new-found bounds
    softSetProperties(this, { _left: left, _right: right });

    const isClosestLeft = !rightIndex.length || p - left < right - p && leftIndex.length;
    indexes = isClosestLeft ? leftIndex : rightIndex;
    const closest = indexes.length ? isClosestLeft ? left : right : null;
    return this._indexes = [ indexes || [], closest ];
  }

  get indexes() {
    return this._closestIndexes[0];
  }

  get closest() {
    return this._closestIndexes[1];
  }
}
